/**
 * Built-in confirmation dialog for DynamicTableV2.
 * Uses the design system's AlertDialog compound component.
 */

import React from 'react';
import { AlertDialog } from '@packages/ui/composites/AlertDialog';

export interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  variant: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
  /** When true: Confirm button shows a spinner and is disabled; Cancel is also disabled. */
  isLoading?: boolean;
  /**
   * `confirm` — cancel + primary (default).
   * `alert` — single primary only (acknowledge then proceed); Escape still cancels without running the action.
   */
  dialogKind?: 'confirm' | 'alert';
  /** Text alignment for the title. Defaults to `'left'`. */
  titleAlign?: 'left' | 'center' | 'right';
  /** Text alignment for the message/description. Defaults to `'left'`. */
  messageAlign?: 'left' | 'center' | 'right';
}

const VARIANT_MAP: Record<ConfirmDialogProps['variant'], 'default' | 'destructive'> = {
  danger: 'destructive',
  warning: 'default',
  info: 'default',
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant,
  onConfirm,
  onCancel,
  isLoading = false,
  dialogKind = 'confirm',
  titleAlign,
  messageAlign,
}) => {
  const confirmedRef = React.useRef(false);

  React.useEffect(() => {
    confirmedRef.current = false;
    return () => {
      confirmedRef.current = false;
    };
  }, [title, message]);

  const handleConfirm = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (confirmedRef.current) return;
      confirmedRef.current = true;
      onConfirm();
    },
    [onConfirm]
  );

  const handleCancel = React.useCallback((_e?: React.MouseEvent | unknown) => {
    if (confirmedRef.current) return;
    onCancel();
  }, [onCancel]);

  return (
    <AlertDialog.Root
      open
      onOpenChange={(open) => {
        if (!open) handleCancel();
      }}
      variant={VARIANT_MAP[variant]}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay />
        <AlertDialog.Content>
          <AlertDialog.Title align={titleAlign}>{title}</AlertDialog.Title>
          {message && <AlertDialog.Description align={messageAlign}>{message}</AlertDialog.Description>}
          <AlertDialog.Footer>
            {dialogKind === 'confirm' && (
              <AlertDialog.Cancel
                semanticId='modalCancel'
                variant='primary-dark'
                onClick={handleCancel}
                disabled={isLoading}
              >
                {cancelLabel}
              </AlertDialog.Cancel>
            )}
            <AlertDialog.Action
              semanticId='modalConfirm'
              icon='check_circle'
              onClick={handleConfirm}
              loading={isLoading}
              closeOnClick={false}
            >
              {confirmLabel}
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
};
